import { Component, createApp, defineAsyncComponent } from "vue";

/**
 * Creates a root DOM element with the given ID if it doesn't already exist.
 * @param id - The ID of the root element.
 * @returns The root DOM element.
 */
export const createRootElement = (id: string): HTMLElement => {
    let root = document.getElementById(id);
    if (!root) {
        root = document.createElement("div");
        root.setAttribute("id", id);
        document.body.appendChild(root);
    }
    return root;
};

/**
 * Dynamically imports and mounts a Vue component.
 * @param id - The ID of the root element.
 * @param componentImportFn - The dynamic import function for the component.
 * @param rootProps - Optional props to pass to the component.
 */
export const mountComponent = <T extends Component>(
    id: string,
    componentImportFn: () => Promise<T>,
    rootProps?: Record<string, unknown> | null
): Promise<T> => {
    const root = createRootElement(id);

    const mountPromise = componentImportFn();
    const component = defineAsyncComponent(componentImportFn);

    const app = createApp(component, rootProps);
    app.mount(root);

    return mountPromise;
};
